/**
 * Glass that the heater has to make up for, taken from the openings the
 * engines actually build — not from a number the user types in.
 *
 * Issue 165: the kW sizing used to read `thermalInputs.glassAreaSqm`, a manual
 * field that defaulted to 0, while `validateGlassPenalty` summed the drawn
 * windows but was then handed the already-computed kW. A fyrkantig with a
 * 1.2 × 1.0 m window was sized as if it had none, and a panorama two-room
 * applied its heat-loss multiplier to that same 0 m².
 *
 * Only glass that bounds the HOT room counts: a relax-room window cools a room
 * the heater never heats. The divider is a wall of the hot room, so a glazed
 * interior door counts in full.
 */
import type { WallOpening } from "../domain/framing/generateFramedWall";
import { TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM } from "./twoRoomConstants";
import { resolveOpeningAdjustmentOpenings } from "./twoRoomOpeningConfig";
import { resolvePartitionSplit } from "./twoRoomSauna";
import type { BuildInput, TwoRoomConfig } from "./types";

export interface HeaterGlassArea {
	/** Windows and glazed doors in the hot room, m². Standard glass factor. */
	openingGlassSqm: number;
	/** The panorama wall's glass in the hot room, m². Panorama glass factor. */
	panoramaGlassSqm: number;
	totalSqm: number;
}

/** The glass fields of `HeaterRequirementsInput`, in its own names. */
export function heaterGlassInput(glass: HeaterGlassArea): {
	glassAreaSqm: number;
	panoramaGlassAreaSqm: number;
} {
	return {
		glassAreaSqm: glass.openingGlassSqm,
		panoramaGlassAreaSqm: glass.panoramaGlassSqm,
	};
}

/** Same rule as `validateGlassPenalty`: every window, and a door only when glazed. */
function isGlass(opening: WallOpening): boolean {
	return opening.type === "window" || opening.glazed === true;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

/**
 * The hot room's span in the side walls' own axis: millimetres from the back
 * (−Z) corner, which is where side-wall `xMm` is measured from
 * (see `deriveInteriorDoors`).
 */
function hotRoomSideSpanMm(
	lengthMm: number,
	saunaLengthMm: number,
	saunaPosition: TwoRoomConfig["saunaPosition"],
): { min: number; max: number } {
	return saunaPosition === "right"
		? { min: lengthMm - saunaLengthMm, max: lengthMm }
		: { min: 0, max: saunaLengthMm };
}

/** Share of the opening's width inside the hot room, 0–1. */
function hotRoomShare(
	opening: WallOpening,
	span: { min: number; max: number },
	saunaPosition: TwoRoomConfig["saunaPosition"],
): number {
	switch (opening.wall) {
		case "divider":
			return 1;
		case "front":
			return saunaPosition === "right" ? 1 : 0;
		case "back":
			return saunaPosition === "right" ? 0 : 1;
		default: {
			if (opening.widthMm <= 0) return 0;
			const overlap =
				Math.min(opening.xMm + opening.widthMm, span.max) -
				Math.max(opening.xMm, span.min);
			return Math.max(0, Math.min(1, overlap / opening.widthMm));
		}
	}
}

function areaSqm(opening: WallOpening): number {
	return (opening.widthMm / 1000) * (opening.heightMm / 1000);
}

/**
 * @param config the resolved two-room config, or undefined for a one-room
 *   engine (every opening then bounds the hot room).
 */
export function resolveHeaterGlassArea(
	input: Pick<BuildInput, "dimensions" | "openings" | "partitionSplit">,
	config?: TwoRoomConfig,
): HeaterGlassArea {
	if (!config) {
		const openingGlassSqm = round2(
			input.openings.filter(isGlass).reduce((sum, o) => sum + areaSqm(o), 0),
		);
		return { openingGlassSqm, panoramaGlassSqm: 0, totalSqm: openingGlassSqm };
	}

	const lengthM = input.dimensions.length;
	const lengthMm = lengthM * 1000;
	const saunaLengthMm = config.hasRelaxRoom
		? lengthMm * resolvePartitionSplit(input.partitionSplit, lengthM, true)
		: lengthMm;

	// The openings as built: panorama-wall windows dropped, a long-wall entry
	// door moved to where the engine frames it.
	const built = resolveOpeningAdjustmentOpenings({
		...(input as BuildInput),
		...config,
	});

	let openingGlass = 0;
	if (config.hasRelaxRoom) {
		const span = hotRoomSideSpanMm(
			lengthMm,
			saunaLengthMm,
			config.saunaPosition,
		);
		for (const opening of built) {
			if (!isGlass(opening)) continue;
			openingGlass +=
				areaSqm(opening) * hotRoomShare(opening, span, config.saunaPosition);
		}
	} else {
		for (const opening of built) {
			if (isGlass(opening) && opening.wall !== "divider") {
				openingGlass += areaSqm(opening);
			}
		}
	}

	const panoramaGlass =
		config.glazingStyle === "panorama"
			? (saunaLengthMm / 1000) * (TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM / 1000)
			: 0;

	const openingGlassSqm = round2(openingGlass);
	const panoramaGlassSqm = round2(panoramaGlass);
	return {
		openingGlassSqm,
		panoramaGlassSqm,
		totalSqm: round2(openingGlassSqm + panoramaGlassSqm),
	};
}
